import { Link } from "react-router-dom";
import { useState } from "react";

const Footer = () => {
  const [hoveredLink, setHoveredLink] = useState(null);

  const currentYear = new Date().getFullYear();

  const footerLinks = {
    navigation: [
      { to: "/", label: "Home" },
      { to: "/movies", label: "Movies" },
      { to: "/add", label: "Add Movie" },
    ],
    social: [
      { name: "Twitter", href: "#", icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" /></svg>
      )},
      { name: "GitHub", href: "#", icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" /></svg>
      )},
      { name: "LinkedIn", href: "#", icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
      )},
    ],
  };

  return (
    <footer className="relative bg-gray-950 border-t border-white/10">
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-t from-orange-500/5 via-transparent to-transparent pointer-events-none"></div>
      
      {/* Main footer content */}
      <div className="relative z-10 container mx-auto px-4 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Brand Section */}
          <div className="space-y-4">
            <Link to="/" className="inline-block group relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-orange-500 to-pink-500 rounded-xl blur-lg opacity-0 group-hover:opacity-40 transition-opacity duration-500"></div>
              <span className="relative text-2xl font-bold bg-gradient-to-r from-orange-400 via-orange-500 to-pink-500 bg-clip-text text-transparent">
                MovieHub
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
              Discover, explore, and share your favorite movies. Your ultimate destination for cinema entertainment.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-2 sm:gap-3 pt-2">
              {footerLinks.social.map((social, index) => (
                <a
                  key={social.name}
                  href={social.href}
                  onMouseEnter={() => setHoveredLink(`social-${index}`)}
                  onMouseLeave={() => setHoveredLink(null)}
                  className="relative p-2 sm:p-2.5 rounded-xl transition-all duration-300 group"
                  aria-label={social.name}
                >
                  {/* Glow effect */}
                  <span className={`absolute inset-0 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 transition-all duration-300 ${
                    hoveredLink === `social-${index}` ? 'opacity-20' : 'opacity-0'
                  }`}></span>
                  {/* Icon */}
                  <span className={`relative block transition-all duration-300 ${
                    hoveredLink === `social-${index}` 
                      ? 'text-white scale-110' 
                      : 'text-gray-400'
                  }`}>
                    <span className="text-sm sm:text-base">{social.icon}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-white font-semibold text-lg sm:text-xl">Quick Links</h3>
            <ul className="space-y-2">
              {footerLinks.navigation.map((link, index) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    onMouseEnter={() => setHoveredLink(`nav-${index}`)}
                    onMouseLeave={() => setHoveredLink(null)}
                    className="group flex items-center gap-2 text-gray-400 hover:text-white transition-all duration-300 text-sm sm:text-base"
                  >
                    {/* Arrow indicator */}
                    <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r from-orange-400 to-pink-500 transition-all duration-300 ${
                      hoveredLink === `nav-${index}` ? 'scale-150' : 'scale-100'
                    }`}></span>
                    <span className="relative">
                      {link.label}
                      {/* Underline animation */}
                      <span className={`absolute -bottom-0.5 left-0 h-0.5 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full transition-all duration-300 ${
                        hoveredLink === `nav-${index}` ? 'w-full' : 'w-0'
                      }`}></span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Section */}
          <div className="space-y-4">
            <h3 className="text-white font-semibold text-lg sm:text-xl">Stay Updated</h3>
            <p className="text-gray-400 text-sm">
              Get the latest movie updates and recommendations delivered to your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-2" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-3 sm:px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 text-sm sm:text-base"
              />
              <button
                type="submit"
                className="px-4 sm:px-5 py-2.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-orange-500/30 hover:scale-105 transition-all duration-300 text-sm sm:text-base"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-xs sm:text-sm text-center sm:text-left">
              {currentYear} MovieHub. All rights reserved.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-xs sm:text-sm">
              <Link to="#" className="text-gray-500 hover:text-white transition-colors duration-300 relative group">
                Privacy Policy
                <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="#" className="text-gray-500 hover:text-white transition-colors duration-300 relative group">
                Terms of Service
                <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
